import React, { createContext, useContext, useState, useEffect } from 'react';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Mock product data
const mockProducts = [
  {
    id: '1',
    name: 'Nikah Saree with Handwritten Dua',
    nameUrdu: 'نکاح ساڑی',
    category: 'bridal-saree',
    price: 15999,
    originalPrice: 19999,
    images: [
      'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&q=80',
      'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=600&q=80',
    ],
    description: 'Elegant bridal saree with hand-embroidered nikah duas and pearl embellishments.',
    features: ['Handwritten duas', 'Pearl details', 'Premium fabric', 'Custom text available'],
    isCustomizable: true,
    inStock: true,
    rating: 4.9,
    reviews: 127,
  },
  {
    id: '2',
    name: 'Custom Lehenga with Quranic Verses',
    nameUrdu: 'لہنگا',
    category: 'bridal-saree',
    price: 24999,
    originalPrice: 29999,
    images: [
      'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=600&q=80',
      'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=600&q=80',
    ],
    description: 'Luxurious lehenga featuring delicate Quranic calligraphy and silver threadwork.',
    features: ['Quranic calligraphy', 'Silver embroidery', 'Customizable text', 'Matching dupatta'],
    isCustomizable: true,
    inStock: true,
    rating: 4.8,
    reviews: 89,
  },
  {
    id: '3',
    name: 'Islamic Marriage Certificate Frame',
    nameUrdu: 'نکاح نامہ فریم',
    category: 'accessories',
    price: 3499,
    originalPrice: 4499,
    images: [
      'https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=600&q=80',
    ],
    description: 'Beautifully designed frame for your nikah certificate with Islamic motifs.',
    features: ['Premium wood', 'Glass protection', 'Islamic patterns', 'Custom engraving'],
    isCustomizable: true,
    inStock: true,
    rating: 4.7,
    reviews: 234,
  },
  {
    id: '4',
    name: 'Pearl & Silver Bridal Set',
    nameUrdu: 'موتی سیٹ',
    category: 'accessories',
    price: 8999,
    originalPrice: 11999,
    images: [
      'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&q=80',
    ],
    description: 'Elegant pearl and silver jewelry set perfect for your nikah day.',
    features: ['Freshwater pearls', 'Silver plated', 'Necklace + Earrings', 'Gift box included'],
    isCustomizable: false,
    inStock: true,
    rating: 4.9,
    reviews: 156,
  },
  {
    id: '5',
    name: 'Custom Dua Gift Box',
    nameUrdu: 'دعا گفٹ باکس',
    category: 'gifts',
    price: 2999,
    originalPrice: 3999,
    images: [
      'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=600&q=80',
    ],
    description: 'Personalized gift box with handwritten duas for the newlyweds.',
    features: ['Handwritten duas', 'Velvet box', 'Rose petals', 'Custom message'],
    isCustomizable: true,
    inStock: true,
    rating: 4.8,
    reviews: 312,
  },
  {
    id: '6',
    name: 'Nikah Memory Book',
    nameUrdu: 'نکاح یادداشت',
    category: 'gifts',
    price: 4499,
    originalPrice: 5999,
    images: [
      'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&q=80',
    ],
    description: 'Beautiful memory book to preserve your nikah moments with Islamic designs.',
    features: ['Premium paper', 'Islamic artwork', 'Photo pockets', 'Couple name embossing'],
    isCustomizable: true,
    inStock: true,
    rating: 4.6,
    reviews: 98,
  },
  {
    id: '7',
    name: 'Bridal Dupatta with Silver Writing',
    nameUrdu: 'دلہن دوپٹہ',
    category: 'bridal-saree',
    price: 6999,
    originalPrice: 8999,
    images: [
      'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=600&q=80',
    ],
    description: 'Luxurious dupatta featuring silver-thread calligraphy of blessed duas.',
    features: ['Pure silk', 'Silver threadwork', 'Custom duas', 'Pearl borders'],
    isCustomizable: true,
    inStock: true,
    rating: 4.9,
    reviews: 203,
  },
  {
    id: '8',
    name: 'Islamic Wedding Invitation Cards',
    nameUrdu: 'شادی کارڈ',
    category: 'gifts',
    price: 1999,
    originalPrice: 2499,
    images: [
      'https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=600&q=80',
    ],
    description: 'Elegant wedding invitation cards with beautiful Islamic calligraphy.',
    features: ['Premium cardstock', 'Gold foiling', 'Set of 50', 'Custom text'],
    isCustomizable: true,
    inStock: true,
    rating: 4.7,
    reviews: 445,
  },
];

const categories = [
  {
    id: 'bridal-saree',
    name: 'Custom Bridal Saree & Lehenga Writing',
    nameUrdu: 'دلہن ساڑی اور لہنگا',
    description: 'Handcrafted bridal wear with personalized duas and calligraphy',
    image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&q=80',
  },
  {
    id: 'accessories',
    name: 'Islamic Marriage Accessories',
    nameUrdu: 'شادی کی چیزیں',
    description: 'Beautiful accessories for your special day',
    image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&q=80',
  },
  {
    id: 'gifts',
    name: 'Islamic Gift Creations',
    nameUrdu: 'اسلامی تحائف',
    description: 'Meaningful gifts filled with barakah',
    image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=600&q=80',
  },
  {
    id: 'new-arrivals',
    name: 'New Arrivals',
    nameUrdu: 'نئی آمد',
    description: 'Latest additions to our collection',
    image: 'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=600&q=80',
  },
];

export const AppProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('fazila_cart');
    const savedWishlist = localStorage.getItem('fazila_wishlist');
    if (savedCart) setCart(JSON.parse(savedCart));
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('fazila_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('fazila_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (product, quantity = 1, customization = null) => {
    setCart(prev => {
      const existingIndex = prev.findIndex(
        item => item.id === product.id && JSON.stringify(item.customization) === JSON.stringify(customization)
      );
      
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }
      
      return [...prev, { ...product, quantity, customization }];
    });
  };

  const removeFromCart = (productId, customization = null) => {
    setCart(prev => prev.filter(
      item => !(item.id === productId && JSON.stringify(item.customization) === JSON.stringify(customization))
    ));
  };

  const updateCartQuantity = (productId, quantity, customization = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, customization);
      return;
    }
    setCart(prev => prev.map(item => 
      item.id === productId && JSON.stringify(item.customization) === JSON.stringify(customization)
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist(prev => {
      const exists = prev.find(item => item.id === product.id);
      if (exists) {
        return prev.filter(item => item.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const isInWishlist = (productId) => wishlist.some(item => item.id === productId);

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const getProductsByCategory = (categoryId) => {
    if (!categoryId || categoryId === 'all') return mockProducts;
    return mockProducts.filter(p => p.category === categoryId);
  };

  const getProductById = (id) => mockProducts.find(p => p.id === id);

  const value = {
    // Products
    products: mockProducts,
    categories,
    getProductsByCategory,
    getProductById,
    
    // Cart
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal,
    cartCount,
    isCartOpen,
    setIsCartOpen,
    
    // Wishlist
    wishlist,
    toggleWishlist,
    isInWishlist,
    
    // User
    user,
    setUser,
    
    // UI
    isMenuOpen,
    setIsMenuOpen,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
